import cn from "classnames";
import { SkillRangeType, SkillType } from "types/battle-engine/battle";
import { MS_PER_TICK } from "types/battle-engine/constants";

import { isXsScreen } from "@/utils/screen-size-check";

import { StrokedText } from "../ui/text/stroked-text";

import type { SkillData } from "types/battle-engine/battle";

// Helper function to convert hex color to rgba string
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Type color mapping (hex values from CSS)
const typeColors: Record<string, string> = {
  normal: "#a8a878",
  fire: "#f08030",
  water: "#6890f0",
  electric: "#f8d030",
  grass: "#78c850",
  ice: "#98d8d8",
  fighting: "#c03028",
  poison: "#a040a0",
  ground: "#e0c068",
  flying: "#a890f0",
  psychic: "#f85888",
  bug: "#a8b820",
  rock: "#b8a038",
  ghost: "#705898",
  dragon: "#7038f8",
  dark: "#705848",
  steel: "#b8b8d0",
  fairy: "#ee99ac",
};

const colorsPerType = {
  fire: {
    top: "from-pk-red-5",
    bottom: "via-pk-red-3/80 to-pk-red-4",
  },
  water: {
    top: "from-pk-blue-5",
    bottom: "via-pk-blue-3/80 to-pk-blue-4",
  },
  grass: {
    top: "from-pk-green-5",
    bottom: "via-pk-green-3/80 to-pk-green-4",
  },
  electric: {
    top: "from-pk-yellow-3",
    bottom: "via-pk-yellow-3/80 to-pk-yellow-2",
  },
  psychic: {
    top: "from-pk-garnet-5",
    bottom: "via-pk-garnet-3/80 to-pk-garnet-4",
  },
  poison: {
    top: "from-pk-purple-5",
    bottom: "via-pk-purple-3/80 to-pk-purple-4",
  },
  ground: {
    top: "from-pk-beige-3",
    bottom: "via-pk-beige-3/80 to-pk-beige-4",
  },
  flying: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/80 to-pk-gray-3",
  },
  bug: {
    top: "from-pk-green-5",
    bottom: "via-pk-green-3/80 to-pk-green-4",
  },
  rock: {
    top: "from-pk-gray-3",
    bottom: "via-pk-gray-3/80 to-pk-gray-3",
  },
  ice: {
    top: "from-pk-blue-5",
    bottom: "via-pk-blue-3/80 to-pk-blue-4",
  },
  dragon: {
    top: "from-pk-red-5",
    bottom: "via-pk-red-3/80 to-pk-red-4",
  },
  fairy: {
    top: "from-pk-garnet-5",
    bottom: "via-pk-garnet-3/80 to-pk-garnet-4",
  },
  dark: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/80 to-pk-gray-4",
  },
  steel: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/80 to-pk-gray-4",
  },
  ghost: {
    top: "from-pk-gray-2",
    bottom: "via-pk-gray-3/80 to-pk-purple-2",
  },
  fighting: {
    top: "from-pk-red-4",
    bottom: "via-pk-gray-3/80 to-pk-red-4",
  },
  normal: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/80 to-pk-gray-4",
  },
};

interface PokemonSkillCardProps {
  skill: SkillData;
  isSelected?: boolean;
  onClick?: () => void;
  isSelectable?: boolean;
  compact?: boolean;
}

export const PokemonSkillCard = ({
  skill,
  isSelected = false,
  onClick,
  isSelectable = false,
  compact = false,
}: PokemonSkillCardProps) => {
  const colors =
    colorsPerType[
      skill.pokemon_type.toLocaleLowerCase() as keyof typeof colorsPerType
    ];

  const typeName = skill.pokemon_type.toLocaleLowerCase();
  const typeColor = typeColors[typeName];
  if (!typeColor) throw new Error(`Type color not found for ${typeName}`);
  const backgroundColor = hexToRgba(typeColor, 1);

  const cooldown =
    Math.round(((skill.cooldown_ticks * MS_PER_TICK) / 1000) * 10) / 10;

  const isArea =
    skill.range.type === SkillRangeType.ALL_ENEMIES ||
    skill.range.type === SkillRangeType.ALL_ALLIES ||
    skill.range.type === SkillRangeType.ANCHORED_INVOKE ||
    skill.range.type === SkillRangeType.TARGETED_INVOKE;

  const isTarget = skill.range.type === SkillRangeType.POINT_TARGET;

  const isHealing = skill.types.includes(SkillType.HEAL);
  const isBuff = skill.types.includes(SkillType.BUFF);
  const isDebuff = skill.types.includes(SkillType.DEBUFF);
  const isShield = skill.types.includes(SkillType.SHIELD);
  const isMelee =
    skill.range.type === SkillRangeType.POINT_TARGET && skill.range.value < 2;
  const isRanged =
    skill.range.type === SkillRangeType.POINT_TARGET && skill.range.value >= 2;

  return (
    <div
      onClick={onClick}
      className={cn("group relative flex flex-col p-0 rounded-sm", {
        "hover:scale-105 cursor-pointer transition-all duration-300 active:scale-95":
          isSelectable,
      })}
    >
      <div className="hidden bottom-0 left-0 z-3 absolute inset-0 bg-gradient-to-t from-pk-fairy via-pk-fairy/30 to-transparent rounded-sm"></div>

      {isSelected && (
        <div className="top-[-10px] right-[-10px] z-20 absolute bg-pk-green-3 shadow-lg px-3 py-1 rounded-md font-semibold text-white text-xs">
          SELECTED
        </div>
      )}

      <div
        className={cn(
          "z-[2] relative flex flex-col gap-2 bg-contain bg-center p-1.5 pt-2 border-b-2 rounded-sm rounded-b-xl w-full h-full overflow-hidden transition-all duration-300",
          "before:content-[''] before:opacity-50 before:absolute before:inset-[-14px] before:z-15 before:pointer-events-none before:bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.10),transparent_82%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.08),transparent_45%),repeating-linear-gradient(135deg,rgba(0,0,0,0.28)_0px,rgba(0,0,0,0.38)_4px,transparent_4px,transparent_7px)]",
          {
            "border-pk-gray-4/80 !border-0 shadow-cartoon": !isSelected,
            "border-pk-green-3 border-4 shadow-[0_0_15px_rgba(100,154,84,0.8)] z-10 scale-[1.02]":
              isSelected,
            "saturate-[0.85] brightness-[0.35] opacity-95":
              isSelectable && !isSelected,
          },
        )}
        style={{
          backgroundImage: `url('/assets/card-art/${skill.id}.jpg')`,
        }}
      >
        <div
          className={`absolute  top-0 left-0 w-full h-full bg-gradient-to-r ${colors.top} to-transparent z-[1]  rounded-sm`}
        ></div>
        <div
          className={`absolute  top-0 left-0 w-full h-full bg-gradient-to-l from-transparent ${colors.bottom} z-[1]  rounded-sm`}
        ></div>
        <div
          className="top-0 left-0 z-[1] absolute opacity-0 rounded-sm w-full h-full"
          style={{
            backgroundImage: "url('/assets/app-ui/textre.png')",
            backgroundSize: "100%",
            backgroundPosition: "center",
            backgroundRepeat: "repeat",
          }}
        ></div>

        <div className="z-20 flex justify-between items-end">
          <StrokedText
            fontSize={isXsScreen() ? 14 : 18}
            strokeWidth={isXsScreen() ? 6 : 8}
            height={5}
            className="z-10 relative ml-2"
          >
            {skill.name}
          </StrokedText>

          <div
            className={`flex flex-row gap-2 shadow-lg outline-2 outline-pk-gray-4 items-center z-[12] rounded-sm bg-pk-${typeName} px-2 py-0.5`}
            style={{
              backgroundColor,
            }}
          >
            <span className="text-xs tracking-tight">{skill.pokemon_type}</span>
          </div>
        </div>

        {/* Skill Tags */}
        <div className="z-25 flex flex-row flex-wrap items-center gap-1">
          {isTarget && (
            <div className="flex items-center gap-1 bg-pk-orange-4 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                🎯
              </span>
              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Targeted
              </span>
            </div>
          )}

          {isMelee && (
            <div className="flex items-center gap-1 bg-pk-red-3 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                👊
              </span>

              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Melee
              </span>
            </div>
          )}

          {isRanged && (
            <div className="flex items-center gap-1 bg-pk-green-3 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                🎯
              </span>

              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Ranged
              </span>
            </div>
          )}

          {isArea && (
            <div className="flex items-center gap-1 bg-pk-orange-4 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                ⬜
              </span>
              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Area Effect
              </span>
            </div>
          )}

          {isHealing && (
            <div className="flex items-center gap-1 bg-pk-green-4 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                🩹
              </span>
              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Healing
              </span>
            </div>
          )}

          {isBuff && (
            <div className="flex items-center gap-1 bg-pk-orange-3 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                💪
              </span>
              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Buff
              </span>
            </div>
          )}

          {isDebuff && (
            <div className="flex items-center gap-1 bg-pk-purple-3 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                �
              </span>
              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Debuff
              </span>
            </div>
          )}

          {isShield && (
            <div className="flex items-center gap-1 bg-pk-blue-3 px-2 py-1 rounded-md">
              <span
                className="font-bold text-pk-gray-5 text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                🛡️
              </span>
              <span
                className="font-semibold text-white text-xs"
                style={{ textShadow: "-1px 1px 1px black" }}
              >
                Shield
              </span>
            </div>
          )}
        </div>

        {!compact && (
          <div className="z-25 bg-black/10 backdrop-blur-[1px] mt-auto px-2 py-1 rounded-md w-full">
            <span
              className="font-medium text-pk-gray-5 text-xs"
              style={{
                textShadow: "-2px 2px 2px black",
              }}
            >
              {skill.description}
            </span>
          </div>
        )}

        <div className="z-25 flex justify-end items-center gap-2">
          <div className="z-25 flex items-center gap-1 bg-pk-gray-2 px-2 py-1 rounded-sm">
            <span
              className="font-bold text-pk-gray-5 text-sm translate-y-[-1px]"
              style={{ textShadow: "-1px 1px 1px black" }}
            >
              🕑
            </span>
            <span
              className="ml-auto font-medium text-white text-sm tracking-widest"
              style={{ textShadow: "-1px 1px 1px black" }}
            >
              {cooldown}s
            </span>
          </div>
        </div>
      </div>

      <div className="bottom-0 left-0 z-[1] absolute bg-pk-gray-1 rounded-b-lg w-full h-4 translate-y-1.5"></div>
    </div>
  );
};
